// TRU-3217: when Descope returns E023001 (User disabled) during login, ask the
// monolith whether this user is actually suspended (vs. some other E023001 cause)
// and if so navigate them to linktr.ee/suspended instead of leaving them stuck
// on the login screen with a generic "failed to sign in" error.
//
// We hit /validate/login/status (POST), which is part of the existing
// /validate/login/* family on the monolith (email, username, phone). The
// monolith resolves login_id → user uuid against its own DB and proxies to
// userdatamgmt for the suspension lookup itself.
//
// The monolith endpoint is fail-open: a downstream userdatamgmt outage returns
// { suspended: false } rather than an error, so we never wrongly redirect an
// active user. We mirror that here — any fetch/parse failure leaves the existing
// error visible and lets the user retry.

import { isMobileApp } from './descopeQueryParams';

const DESCOPE_DISABLED_ERROR_CODE = 'E023001';

// MES-875: password login surfaces a generic message in context.error.text and
// (unlike OAuth) does NOT inline the E023001 code or use the
// 'OTPSignUpOrInEmailFailed' type, so neither existing signal fires and the
// disabled user is left stuck on the login screen. The Descope flow has since
// been configured to emit the literal text "User disabled" for this case, so we
// substring-match it (case-insensitively) as an additional candidate. Like the
// other signals this is only a *candidate* — the monolith lookup disambiguates.
const DESCOPE_DISABLED_ERROR_TEXT = 'user disabled';

// Reason string passed to descopeBridge.abortFlow on mobile. Cerberus's
// DescopeFlowView onError handler matches on this exact value (surfaced as
// `errorMessage` on the error payload) to navigate the user to /suspended in
// the system browser. Keep in sync with the cerberus side.
export const MOBILE_SUSPENDED_ABORT_REASON = 'SUSPENDED';

interface DescopeBridge {
  abortFlow?: (reason: string) => void;
}

// Password/OTP login: Descope strips the structured error code by the time it
// reaches onScreenUpdate and only exposes `context.error.type`. The type
// 'OTPSignUpOrInEmailFailed' fires for other failures too (wrong identifier,
// generic OTP failure, etc.), so it's a *candidate* for disabled-user, not a
// confirmation — we still need the monolith lookup to disambiguate.
const PASSWORD_OTP_DISABLED_ERROR_TYPE = 'OTPSignUpOrInEmailFailed';

interface DescopeScreenError {
  // password/OTP: text is a generic message, type carries the failure category
  // OAuth: text inlines the structured error string verbatim, e.g.
  //   "Failed to sign up or in: [E023001] User is disabled: Disabled user in oauth exchange"
  text?: string;
  type?: string;
  // Older / less common shape — kept defensively in case some flows surface
  // the error as a top-level string field instead of embedding it in text.
  error?: string;
}

// Returns true if the screen-update error *could* indicate a disabled user.
// False positives are OK here — the monolith lookup is the source of truth.
export function isPotentiallyDisabledUserError(
  error: DescopeScreenError | undefined | null
): boolean {
  if (!error) {
    return false;
  }

  // OAuth: Descope inlines "[E023001] User is disabled..." into error.text on
  // OAuthExchangeCodeFailed. Password login (MES-875) instead surfaces the
  // literal "User disabled" text with no code. Substring-match both without
  // coupling to every type name Descope might use across providers.
  const inlineCandidates = [error.text, error.error];
  if (
    inlineCandidates.some(value => {
      if (typeof value !== 'string') {
        return false;
      }
      return (
        value.includes(DESCOPE_DISABLED_ERROR_CODE) ||
        value.toLowerCase().includes(DESCOPE_DISABLED_ERROR_TEXT)
      );
    })
  ) {
    return true;
  }

  return error.type === PASSWORD_OTP_DISABLED_ERROR_TYPE;
}

// Returns true if the error unambiguously identifies a disabled user.
// Used in onError (flow-level errors, which is where OAuth disabled-user errors
// surface). Unlike isPotentiallyDisabledUserError, this won't fire for any other
// failure type — the OAuth error string carries E023001 verbatim, so a hit here
// is treated as confirmation and the caller can skip the monolith lookup if it
// has no loginId to send. Checks a handful of common field names because the
// Descope SDK has serialized this error in slightly different shapes across the
// web-component and core-js layers (`error`, `errorMessage`, `errorDescription`,
// `message`).
export function isConfirmedDisabledError(
  detail: Record<string, unknown> | null | undefined
): boolean {
  if (!detail) {
    return false;
  }

  const candidates = [
    detail.error,
    detail.errorMessage,
    detail.errorDescription,
    detail.message,
  ];

  return candidates.some(
    value =>
      typeof value === 'string' && value.includes(DESCOPE_DISABLED_ERROR_CODE)
  );
}

const OAUTH_CANCELLED_ERROR_TYPE_PATTERN = /^OAuthCancell?ed(?:ByUser)?$/i;
const OAUTH_TEXT_PATTERN = /oauth/i;
const CANCELLED_TEXT_PATTERN = /cancell?ed/i;

const isRecordValue = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

// Returns true when Descope reports a user-canceled OAuth exchange. This is a
// benign retry state, not an auth failure: the React shell should clear the
// screen error and keep the live `next` callback so the user can tap Google or
// Apple again.
export function isOAuthCanceledByUserError(detail: unknown): boolean {
  if (!detail) {
    return false;
  }

  if (typeof detail === 'string') {
    return (
      OAUTH_TEXT_PATTERN.test(detail) && CANCELLED_TEXT_PATTERN.test(detail)
    );
  }

  if (!isRecordValue(detail)) {
    return false;
  }

  const nestedDetail = isRecordValue(detail.detail) ? detail.detail : undefined;
  const typeCandidates = [
    detail.errorType,
    detail.type,
    nestedDetail?.errorType,
    nestedDetail?.type,
  ];
  if (
    typeCandidates.some(
      value =>
        typeof value === 'string' &&
        OAUTH_CANCELLED_ERROR_TYPE_PATTERN.test(value)
    )
  ) {
    return true;
  }

  const candidates = [
    detail.error,
    detail.errorMessage,
    detail.errorDescription,
    detail.message,
    detail.text,
    typeof detail.detail === 'string' ? detail.detail : undefined,
    nestedDetail?.error,
    nestedDetail?.errorMessage,
    nestedDetail?.errorDescription,
    nestedDetail?.message,
    nestedDetail?.text,
  ];
  const hasOAuthTypedContext = typeCandidates.some(
    value => typeof value === 'string' && OAUTH_TEXT_PATTERN.test(value)
  );

  return candidates.some(value => {
    if (typeof value !== 'string') {
      return false;
    }

    const isCancelled = CANCELLED_TEXT_PATTERN.test(value);
    if (!isCancelled) {
      return false;
    }

    return hasOAuthTypedContext || OAUTH_TEXT_PATTERN.test(value);
  });
}

interface LastAuthLike {
  loginId?: string;
}

interface FormLike {
  [key: string]: unknown;
  'form.emailOrUsername'?: string;
}

// Pulls the identifier we ask the monolith to look up. We prefer the value the
// user actually typed on this attempt (form.emailOrUsername) over
// lastAuth.loginId. lastAuth is Descope's "remembered" last-authenticated user
// from a *previous* session: it can be stale or belong to a different account
// than the one being signed into now, which makes the monolith resolve the
// wrong user and report a suspended account as active — the user is then left
// stuck on the login screen instead of being sent to /suspended (TRU-3392).
// lastAuth.loginId is kept as a last resort for flows where no form value is
// available (e.g. OAuth, where the visitor never typed an identifier).
export function extractLoginId(
  context: { lastAuth?: LastAuthLike; form?: FormLike } | null | undefined,
  fallbackForm: FormLike | null | undefined
): string | null {
  const candidates = [
    context?.form?.['form.emailOrUsername'],
    fallbackForm?.['form.emailOrUsername'],
    context?.lastAuth?.loginId,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.length > 0) {
      return candidate;
    }
  }

  return null;
}

// Hits monolith /validate/login/status. Returns false on any error (network,
// parse, non-2xx) — mirrors the fail-open contract of the upstream endpoint.
// POSTs with form-encoded body to match the rest of the /validate/login/*
// family on the monolith (email/username/phone all accept form-encoded params).
async function fetchSuspensionStatus(
  loginId: string,
  baseUrl: string
): Promise<boolean> {
  const url = `${baseUrl}/validate/login/status`;
  // The monolith forwards login_id straight to Descope's user->load, whose
  // lookup treats a literal '+' as a space — so an email like
  // rawan+testingage4@linktr.ee resolves to no user and a suspended account is
  // reported active, leaving the user stuck on login (TRU-3392). To make the
  // '+' survive, the value must still be percent-encoded *after* the monolith's
  // form-decode. We pre-encode with encodeURIComponent, then URLSearchParams
  // adds the transport layer, so the body is double-encoded on the wire
  // (login_id=rawan%252Btestingage4%2540linktr.ee). The monolith form-decodes
  // once back to rawan%2Btestingage4%40linktr.ee, which Descope then decodes to
  // the real identifier. Verified against monolith /validate/login/status: only
  // the double-encoded payload resolves a '+' email to a suspended user.
  const body = new URLSearchParams();
  body.set('login_id', encodeURIComponent(loginId));

  try {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body,
    });
    if (!response.ok) {
      return false;
    }

    const json = (await response.json()) as { suspended?: unknown };

    return json.suspended === true;
  } catch {
    return false;
  }
}

// Side-effecting helper called from onScreenUpdate. Performs the suspension
// check and, on confirmation, navigates to /suspended. Callers should guard
// against firing more than once per disabled-error event (a ref/Set keyed on
// loginId is the typical pattern, since onScreenUpdate can re-fire with the
// same context during React re-renders).
export async function redirectIfSuspended(
  loginId: string,
  baseUrl: string
): Promise<boolean> {
  const suspended = await fetchSuspensionStatus(loginId, baseUrl);
  if (suspended) {
    redirectToSuspended(baseUrl);

    return true;
  }

  return false;
}

// Direct navigation to /suspended, no monolith check. Used by the onError path
// for OAuth disabled-user errors where Descope has already confirmed E023001
// in the error string — the SDK's narrowed detection is its own disambiguation
// and the OAuth flow doesn't expose a loginId to send to the monolith anyway
// (the user clicked a provider button, never typed an identifier).
//
// Inside the cerberus mobile app, a JS-initiated window.location.href is
// classified as navigationType=.other by WKWebView and stays trapped in the
// auth WebView (separate cookie jar from the system browser, so /suspended
// renders broken). Instead we call descopeBridge.abortFlow, which the Descope
// SDK exposes inside the WebView and routes to cerberus's onError handler with
// our reason string — cerberus then opens /suspended in the system browser
// where the user has a real session.
export function redirectToSuspended(baseUrl: string): void {
  const bridge = (window as Window & { descopeBridge?: DescopeBridge })
    .descopeBridge;
  if (isMobileApp() && bridge?.abortFlow) {
    bridge.abortFlow(MOBILE_SUSPENDED_ABORT_REASON);

    return;
  }

  window.location.href = `${baseUrl}/suspended`;
}
